/**
 * Booking service catalog — the canonical list of services a customer
 * can pick on the booking page, with each service's allowed locations
 * (appointment vs dropoff vs both) and whether it requires a deposit
 * flow.
 *
 * Extracted from /book/page.tsx so multiple components (WigList,
 * future unified booking page) can share the same source of truth.
 *
 * If you add a new service:
 *   1. Add the entry below.
 *   2. Confirm /api/appointments/route.ts whitelists the type.
 *   3. Confirm /api/appointments/available-slots/route.ts knows its
 *      duration (or it falls back to slotDurationMinutes).
 *   4. Confirm src/lib/sms.ts type-label maps include it.
 *   5. Update src/lib/constants.ts APPOINTMENT_DEPOSITS if it carries
 *      a deposit.
 */

export type LocationChoice = "appointment" | "dropoff";

export interface ServiceDef {
  id: string;
  label: string;
  description: string;
  allowedLocations: LocationChoice[] | "both";
  /**
   * If true, this service's deposit must be paid on the deposit step;
   * /book historically redirected these to /consultation. Today only
   * `new_wig`. After the consolidation lands, this means "render the
   * SquareCardForm inline on the confirm step".
   */
  depositRedirect?: boolean;
  /**
   * If true, the service is willing to "ride along" with another
   * appointment-allowing service in the same wig — but cannot drive
   * an appointment by itself. Use for services that normally require
   * a drop-off (repair, resize) but can be done quickly during a
   * wait if the customer is already in the salon for something else
   * (e.g. wash + repair, color + resize).
   */
  allowAppointmentWhenCombined?: boolean;
}

export const SERVICES: ServiceDef[] = [
  {
    id: "new_wig",
    label: "New wig consultation",
    description:
      "Meet with Rivky to design a custom wig. $150 deposit required — credited at purchase.",
    allowedLocations: ["appointment"],
    depositRedirect: true,
  },
  {
    id: "wash_set",
    label: "Wash & Set",
    description: "Salon wash + blow-out or set.",
    // Both (Rivky 2026-07-26, reversing the 2026-06-28 drop-off-only
    // rule). Most customers drop off and collect a few days later — that
    // stays the default. But a customer who wants it styled ON her head,
    // wants to add other services, or is new and needs to be taught the
    // style has to be able to book a chair. Rivky was already doing this
    // by hand: 88 of 244 salon-service appointments have no drop-off
    // ticket behind them at all.
    allowedLocations: "both",
  },
  {
    id: "restyle",
    label: "New wig cut / recut",
    description: "Reshape, cut, layers, or new styling.",
    allowedLocations: "both",
  },
  {
    id: "color",
    label: "Color service",
    description: "Roots, highlights, or full color with wig-safe dyes.",
    allowedLocations: "both",
  },
  {
    id: "deep_clean",
    label: "Keratin treatment",
    description: "Deep conditioning + straightening treatment.",
    allowedLocations: "both",
  },
  {
    id: "repair",
    label: "Repair",
    description: "Torn lace, loose wefts, broken clips, damaged hairline.",
    // Drop-off by default — repairs typically take time. But if the
    // customer is already at the salon for a wait-in-salon service,
    // a minor repair can ride along (loose weft, broken clip).
    allowedLocations: ["dropoff"],
    allowAppointmentWhenCombined: true,
  },
  {
    id: "resize",
    label: "Resize",
    description: "Adjust cap size for a better fit.",
    // Same rule as repair — drop-off alone, can ride along when
    // combined with a wait-in-salon service. Bug #134 — picking
    // resize alone showed a wait button that the server rejected.
    allowedLocations: ["dropoff"],
    allowAppointmentWhenCombined: true,
  },
];

export function allowed(svc: ServiceDef, loc: LocationChoice): boolean {
  if (svc.allowedLocations === "both") return true;
  return svc.allowedLocations.includes(loc);
}

export interface WigLine {
  /** Local client key — not sent to API. */
  key: string;
  wigName: string;
  /** Service ids picked on this wig. */
  selectedServices: string[];
  location: LocationChoice | null;
  /**
   * Reference photos for THIS wig (Supabase URLs). Customer attaches
   * them via the inline upload widget on each wig row in WigList. On
   * submit, drop-off wigs send their photos to
   * servicePickup.serviceItems[i].photos and the server aggregates
   * them into pickup.photos.intake; appointment-mode wigs aggregate
   * their photos into appointment.attachments.
   */
  photos: string[];
  /**
   * Per-service sub-tier pick (Rivky #151). Map of service id → tier
   * name (e.g. {"color": "Highlights", "restyle": "Layered Cut"}).
   * Optional — only services with a populated catalog
   * (retailColorServices / retailCutServices / retailRepairServices)
   * surface a tier picker. Used to refine the booking + show admin
   * which specific tier the customer wants.
   */
  subTierByService?: Record<string, string>;
}

export function emptyWig(defaultLoc: LocationChoice | null): WigLine {
  return {
    key: Math.random().toString(36).slice(2),
    wigName: "",
    selectedServices: [],
    location: defaultLoc,
    photos: [],
    subTierByService: {},
  };
}
